import type { LiveSessionEvent } from './types';

export const toMs = (iso: string): number => new Date(iso).getTime();

const pad = (n: number) => String(n).padStart(2, '0');

// `t+03:15` relative to the session start; hours appear only once the session runs past one.
export const relativeLabel = (iso: string, startIso: string): string => {
  const diff = Math.round((toMs(iso) - toMs(startIso)) / 1000);
  const sign = diff < 0 ? '-' : '+';
  const abs = Math.abs(diff);
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  return hours > 0
    ? `t${sign}${hours}:${pad(minutes)}:${pad(seconds)}`
    : `t${sign}${pad(minutes)}:${pad(seconds)}`;
};

export const absoluteLabel = (iso: string): string => {
  const date = new Date(iso);
  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit', hour12: false, minute: '2-digit', second: '2-digit',
  });
  return `${time}.${String(date.getMilliseconds()).padStart(3, '0')}`;
};

export const formatSeconds = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${pad(seconds % 60)}s`;
  return `${Math.floor(minutes / 60)}h ${pad(minutes % 60)}m`;
};

// Positive when the client clock ran ahead of the server-normalized time.
export const skewSeconds = (event: LiveSessionEvent): number | null => {
  if (!event.client_occurred_at) return null;
  return Math.round((toMs(event.client_occurred_at) - toMs(event.occurred_at)) / 1000);
};
